import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useFormik, FormikProvider } from "formik";
import {
  Button,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import BoqItemsFieldArray from "components/common/BoqItemsFieldArray";

function axiosMessage(err: unknown, fallback: string): string {
  return (
    (err as { response?: { data?: { message?: string } } })?.response?.data
      ?.message ??
    (err as Error)?.message ??
    fallback
  );
}

/** A freshly-added, never-touched line — dropped before submit rather than persisted as a blank row. */
function isBlankBoqRow(row: unknown): boolean {
  const r = (row ?? {}) as Record<string, unknown>;
  const desc = String(r.description ?? "").trim();
  const qty = Number(r.quantity);
  const rate = Number(r.rate);
  return (
    desc === "" &&
    (!Number.isFinite(qty) || qty <= 1) &&
    (!Number.isFinite(rate) || rate === 0)
  );
}

type ApiResult = { success: boolean; message?: string };

/**
 * Optional BOQ line-item editing, scoped to this one action — this is the
 * only path Accountant/Marketing have to correct a completed job's line
 * items, since Post-Completion Editing keeps the general edit form's item
 * section Super-Admin-only. Omit entirely to leave items untouched.
 */
export type RegenerateInvoiceItemsEditor<Row> = {
  initialRows: Row[];
  getDefaultRow: () => Row;
  /** Convert edited BOQ rows into this module's regenerate-invoice `items` payload shape. */
  toPayloadItems: (rows: Row[]) => unknown[];
};

type Props<Row> = {
  show: boolean;
  onClose: () => void;
  onSuccess: () => void | Promise<void>;
  /** Current final amount, prefilled into the input. */
  currentFinalAmount?: number | null;
  submit: (payload: {
    finalAmount: number;
    reason?: string | null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    items?: any[];
  }) => Promise<ApiResult>;
  itemsEditor?: RegenerateInvoiceItemsEditor<Row>;
};

export default function RegenerateInvoiceModal<Row = never>({
  show,
  onClose,
  onSuccess,
  currentFinalAmount,
  submit,
  itemsEditor,
}: Props<Row>) {
  const [finalAmount, setFinalAmount] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [editItemsOpen, setEditItemsOpen] = useState(false);

  const itemsFormik = useFormik<{ items: Row[] }>({
    enableReinitialize: true,
    initialValues: { items: itemsEditor?.initialRows ?? [] },
    onSubmit: () => {},
  });

  useEffect(() => {
    if (show) {
      setFinalAmount(
        currentFinalAmount != null ? String(currentFinalAmount) : "",
      );
      setReason("");
      setEditItemsOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, currentFinalAmount]);

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleSubmit = async () => {
    const parsed = Number(finalAmount);
    if (finalAmount.trim() === "" || !Number.isFinite(parsed) || parsed < 0) {
      toast.error("Please enter a valid final amount.");
      return;
    }

    let items: unknown[] | undefined;
    if (itemsEditor && editItemsOpen) {
      const rows = itemsFormik.values.items.filter(
        (row) => !isBlankBoqRow(row),
      );
      if (rows.length === 0) {
        toast.error(
          "Add at least one line item, or collapse line-item editing to leave existing items unchanged.",
        );
        return;
      }
      const invalidRow = rows.find((row) => {
        const r = row as Record<string, unknown>;
        const qty = Number(r.quantity);
        return (
          String(r.description ?? "").trim() === "" ||
          !Number.isFinite(qty) ||
          qty <= 0
        );
      });
      if (invalidRow) {
        toast.error(
          "Every line item needs a description and a quantity greater than 0.",
        );
        return;
      }
      items = itemsEditor.toPayloadItems(rows);
    }

    setSubmitting(true);
    try {
      const res = await submit({
        finalAmount: parsed,
        reason: reason.trim() || undefined,
        ...(items ? { items } : {}),
      });
      if (res.success) {
        toast.success(res.message ?? "Invoice regenerated.");
        onClose();
        await onSuccess();
      } else {
        toast.error(res.message ?? "Could not regenerate invoice.");
      }
    } catch (err: unknown) {
      toast.error(axiosMessage(err, "Could not regenerate invoice."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={show}
      toggle={handleClose}
      centered
      size={itemsEditor && editItemsOpen ? "lg" : undefined}
    >
      <ModalHeader toggle={handleClose}>Regenerate invoice</ModalHeader>
      <ModalBody>
        <div className="alert alert-warning py-2 px-3 small mb-3">
          Corrects the billed amount. If there's no active invoice yet, a new
          one is generated from the amount below. If the active invoice
          already has a payment or settlement applied, that same invoice's
          total is corrected in place instead — its original line breakdown
          is left as-is, only the total changes.
        </div>
        <Label className="form-label">Corrected final amount</Label>
        <Input
          type="number"
          min={0}
          step="0.01"
          placeholder="e.g. 15000"
          value={finalAmount}
          onChange={(e) => setFinalAmount(e.target.value)}
          disabled={submitting}
        />
        <Label className="form-label mt-3">
          Reason <span className="text-muted fw-normal">(optional)</span>
        </Label>
        <Input
          type="textarea"
          rows={3}
          placeholder="Why is this invoice being corrected?"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={submitting}
        />

        {itemsEditor ? (
          <div className="mt-3">
            <button
              type="button"
              className="btn btn-link btn-sm p-0 text-decoration-none"
              onClick={() => setEditItemsOpen((v) => !v)}
              disabled={submitting}
            >
              <i
                className={`ri-${editItemsOpen ? "subtract" : "add"}-line align-bottom me-1`}
              />
              {editItemsOpen
                ? "Hide line items"
                : "Also correct line items"}
            </button>
            {editItemsOpen ? (
              <div className="mt-2">
                <div className="alert alert-info py-2 px-3 small mb-2">
                  This replaces the record's own stored line items, not just
                  this invoice — leave it collapsed to only change the
                  amount.
                </div>
                <FormikProvider value={itemsFormik}>
                  <BoqItemsFieldArray
                    validation={itemsFormik}
                    embedded
                    addLineLabel="Add line item"
                    getDefaultRow={
                      itemsEditor.getDefaultRow as unknown as () => {
                        type: any;
                        description: string;
                        quantity: number;
                        unit: string;
                        rate: number;
                      }
                    }
                  />
                </FormikProvider>
              </div>
            ) : null}
          </div>
        ) : null}
      </ModalBody>
      <ModalFooter>
        <Button color="light" disabled={submitting} onClick={handleClose}>
          Cancel
        </Button>
        <Button
          color="primary"
          disabled={submitting}
          onClick={() => void handleSubmit()}
        >
          {submitting ? "Regenerating…" : "Regenerate invoice"}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
